/* cpap-shipping-policy.js
   Interactive behaviour for the Shipping & delivery page:
   (1) shipping estimator  — postcode + cart total -> cost, transit, ETA
   (2) delivery-zone map    — hover / click / focus a state -> detail panel
   Vanilla JS port of the React design handoff. Rates are read from
   data-* attributes on .cdw-ship (set from the section schema settings).
   Self-contained, defensive (no-ops if markup is missing). */
(function () {
  "use strict";

  function init(root) {
    if (!root || root.dataset.shipInit === "1") return;
    root.dataset.shipInit = "1";

    var rates = {
      standard: parseFloat(root.dataset.standard) || 14.95,
      express: parseFloat(root.dataset.express) || 20,
      nextday: parseFloat(root.dataset.nextday) || 19.95,
      threshold: parseFloat(root.dataset.threshold) || 200
    };
    var cutoff = root.dataset.cutoff || "1pm ACST";

    initEstimator(root, rates, cutoff);
    initMap(root);
  }

  /* ─────────── estimator ─────────── */

  var STATE_NAMES = {
    NSW: "New South Wales", VIC: "Victoria", QLD: "Queensland", SA: "South Australia",
    WA: "Western Australia", TAS: "Tasmania", NT: "Northern Territory", ACT: "Aust. Capital Territory"
  };

  function postcodeToState(pc) {
    var n = parseInt(pc, 10);
    if (!isFinite(n)) return null;
    if (n >= 200 && n <= 299) return "ACT";
    if (n >= 800 && n <= 999) return "NT";
    if (n >= 1000 && n <= 2599) return "NSW";
    if (n >= 2600 && n <= 2618) return "ACT";
    if (n >= 2619 && n <= 2899) return "NSW";
    if (n >= 2900 && n <= 2920) return "ACT";
    if (n >= 2921 && n <= 2999) return "NSW";
    if (n >= 3000 && n <= 3999) return "VIC";
    if (n >= 4000 && n <= 4999) return "QLD";
    if (n >= 5000 && n <= 5999) return "SA";
    if (n >= 6000 && n <= 6999) return "WA";
    if (n >= 7000 && n <= 7999) return "TAS";
    return null;
  }

  function isAdelaideMetro(pc) {
    var n = parseInt(pc, 10);
    return n >= 5000 && n <= 5199;
  }

  function isMetro(pc, state) {
    var n = parseInt(pc, 10);
    if (state === "NSW") return (n >= 2000 && n <= 2249) || (n >= 2760 && n <= 2786);
    if (state === "VIC") return n >= 3000 && n <= 3207;
    if (state === "QLD") return (n >= 4000 && n <= 4179) || (n >= 4300 && n <= 4305);
    if (state === "SA") return n >= 5000 && n <= 5199;
    if (state === "WA") return n >= 6000 && n <= 6199;
    if (state === "TAS") return n >= 7000 && n <= 7050;
    if (state === "ACT") return true;
    if (state === "NT") return n >= 800 && n <= 832;
    return false;
  }

  var ZONE_TABLE = {
    SA_METRO:  { stdMin: 1, stdMax: 2, expMin: 1, expMax: 1, sur: 0 },
    SA_REG:    { stdMin: 2, stdMax: 4, expMin: 1, expMax: 2, sur: 0 },
    VIC_METRO: { stdMin: 2, stdMax: 3, expMin: 1, expMax: 2, sur: 0 },
    VIC_REG:   { stdMin: 3, stdMax: 5, expMin: 2, expMax: 3, sur: 0 },
    NSW_METRO: { stdMin: 2, stdMax: 4, expMin: 1, expMax: 2, sur: 0 },
    NSW_REG:   { stdMin: 3, stdMax: 6, expMin: 2, expMax: 3, sur: 0 },
    ACT_METRO: { stdMin: 2, stdMax: 3, expMin: 1, expMax: 2, sur: 0 },
    QLD_METRO: { stdMin: 3, stdMax: 5, expMin: 2, expMax: 3, sur: 0 },
    QLD_REG:   { stdMin: 4, stdMax: 7, expMin: 3, expMax: 4, sur: 0 },
    WA_METRO:  { stdMin: 4, stdMax: 6, expMin: 3, expMax: 4, sur: 0 },
    WA_REG:    { stdMin: 5, stdMax: 9, expMin: 4, expMax: 6, sur: 9.95 },
    TAS_METRO: { stdMin: 4, stdMax: 6, expMin: 3, expMax: 4, sur: 0 },
    TAS_REG:   { stdMin: 5, stdMax: 8, expMin: 4, expMax: 5, sur: 0 },
    NT_METRO:  { stdMin: 5, stdMax: 8, expMin: 4, expMax: 6, sur: 0 },
    NT_REG:    { stdMin: 7, stdMax: 12, expMin: 5, expMax: 8, sur: 14.95 }
  };

  function zoneKey(state, pc) {
    if (!state) return null;
    if (state === "ACT") return "ACT_METRO";
    return state + "_" + (isMetro(pc, state) ? "METRO" : "REG");
  }

  function businessDaysFromNow(n) {
    var d = new Date();
    var added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      var dow = d.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    return d;
  }

  function fmtDate(d) {
    try {
      return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
    } catch (e) {
      return d.toDateString();
    }
  }

  function money(n) { return "$" + Number(n).toFixed(2); }

  function initEstimator(root, rates, cutoff) {
    var form = root.querySelector("[data-ship-estimator]");
    if (!form) return;

    var pcInput = form.querySelector("[data-ship-pc]");
    var cartInput = form.querySelector("[data-ship-cart]");
    var stateHint = form.querySelector("[data-ship-pc-state]");
    var stateSuffix = form.querySelector("[data-ship-pc-suffix]");
    var toggleBtns = Array.prototype.slice.call(form.querySelectorAll("[data-opt]"));

    var els = {
      label: form.querySelector("[data-res-label]"),
      price: form.querySelector("[data-res-price]"),
      spend: form.querySelector("[data-res-spend]"),
      free: form.querySelector("[data-res-free]"),
      eta: form.querySelector("[data-res-eta]"),
      dest: form.querySelector("[data-res-dest]"),
      transit: form.querySelector("[data-res-transit]"),
      carrier: form.querySelector("[data-res-carrier]")
    };

    var option = "standard";

    function setOption(opt) { option = opt; render(); }

    toggleBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var opt = btn.getAttribute("data-opt");
        if (opt === "nextday" && !isAdelaideMetro(pcInput.value)) return;
        setOption(opt);
      });
    });

    if (pcInput) {
      pcInput.addEventListener("input", function () {
        pcInput.value = pcInput.value.replace(/\D/g, "").slice(0, 4);
        if (option === "nextday" && !isAdelaideMetro(pcInput.value)) option = "standard";
        render();
      });
    }
    if (cartInput) cartInput.addEventListener("input", render);

    function render() {
      var pc = pcInput ? pcInput.value : "";
      var cartNum = cartInput ? (Number(cartInput.value) || 0) : 0;
      var state = postcodeToState(pc);
      var zk = zoneKey(state, pc);
      var zone = zk ? ZONE_TABLE[zk] : null;
      var isAdl = isAdelaideMetro(pc);

      // postcode field chrome
      if (stateHint) stateHint.textContent = state ? STATE_NAMES[state] : "Not recognised";
      if (stateSuffix) {
        stateSuffix.textContent = state || "";
        stateSuffix.style.display = state ? "" : "none";
      }

      // next-day availability on the toggle
      toggleBtns.forEach(function (btn) {
        var opt = btn.getAttribute("data-opt");
        var priceEl = btn.querySelector(".price");
        if (opt === "standard" && priceEl) priceEl.textContent = cartNum >= rates.threshold ? "Free" : money(rates.standard);
        if (opt === "express" && priceEl) priceEl.textContent = money(rates.express);
        if (opt === "nextday") {
          btn.classList.toggle("disabled", !isAdl);
          btn.setAttribute("title", isAdl ? "Available" : "Adelaide metro only");
          if (priceEl) priceEl.textContent = isAdl ? money(rates.nextday) : "Adelaide only";
        }
        btn.classList.toggle("on", opt === option);
      });

      // cost + transit per option
      var cost = 0, stdMin, stdMax, label, carrier;
      if (option === "standard") {
        var free = cartNum >= rates.threshold;
        cost = free ? 0 : rates.standard + (zone ? zone.sur : 0);
        stdMin = zone ? zone.stdMin : 3; stdMax = zone ? zone.stdMax : 7;
        label = "Standard Shipping"; carrier = "StarTrack Road Express";
      } else if (option === "express") {
        cost = rates.express + (zone ? zone.sur : 0);
        stdMin = zone ? zone.expMin : 2; stdMax = zone ? zone.expMax : 4;
        label = "Express Shipping"; carrier = "StarTrack Express";
      } else { // nextday
        cost = rates.nextday; stdMin = 1; stdMax = 1;
        label = "Next-Day Adelaide"; carrier = "Local courier (15km of Morphett Vale)";
      }

      var isFree = option === "standard" && cartNum >= rates.threshold;
      var toFree = Math.max(0, rates.threshold - cartNum);

      // ETA window
      var windowTxt = "Enter a postcode";
      if (state) {
        var aMin = businessDaysFromNow(stdMin);
        var aMax = businessDaysFromNow(stdMax);
        windowTxt = stdMin === stdMax ? fmtDate(aMin) : (fmtDate(aMin) + " – " + fmtDate(aMax));
      }

      // paint
      if (els.label) els.label.textContent = label;
      if (els.price) {
        els.price.innerHTML = isFree ? "Free" : (money(cost) + " <small>AUD</small>");
      }
      if (els.spend) {
        if (option === "standard" && !isFree && toFree > 0) {
          els.spend.innerHTML = "Add <b>" + money(toFree) + "</b> for free standard shipping";
          els.spend.hidden = false;
        } else {
          els.spend.hidden = true;
        }
      }
      if (els.free) els.free.hidden = !isFree;
      if (els.eta) els.eta.innerHTML = "<b>" + windowTxt + "</b>";
      if (els.dest) els.dest.textContent = state ? (STATE_NAMES[state] + " · " + (isMetro(pc, state) ? "Metro" : "Regional")) : "—";
      if (els.transit) {
        els.transit.textContent = state
          ? ((stdMin === stdMax ? stdMin : stdMin + "–" + stdMax) + " business day" + (stdMax > 1 ? "s" : ""))
          : "—";
      }
      if (els.carrier) els.carrier.textContent = carrier;
    }

    render();
  }

  /* ─────────── map ─────────── */

  var REGION_DATA = {
    WA:  { fullName: "Western Australia", std: "4–7", exp: "3–5", notes: "Largest delivery zone in Australia. Perth metro typically lands within 4–5 days; Kalgoorlie, Broome and rural WA can take longer." },
    NT:  { fullName: "Northern Territory", std: "5–8", exp: "4–6", notes: "Darwin metro within 5–6 business days. Remote communities, Tiwi Islands and Alice Springs may add 1–4 days." },
    QLD: { fullName: "Queensland", std: "3–5", exp: "2–3", notes: "Brisbane and Gold Coast metro typically arrive within 3 business days. Cairns, Townsville and far-north QLD add 1–2 days." },
    SA:  { fullName: "South Australia", std: "1–2", exp: "1", notes: "Adelaide metro is our home base — same-day dispatch on orders placed before 1pm ACST. Next-day delivery available within 15km of Morphett Vale.", isOrigin: true },
    NSW: { fullName: "New South Wales / ACT", std: "2–4", exp: "1–2", notes: "Sydney metro and the ACT typically arrive within 2–3 business days. Regional NSW including Newcastle, Wollongong and the Hunter add 1–2 days." },
    VIC: { fullName: "Victoria", std: "2–3", exp: "1–2", notes: "Melbourne metro within 2 business days for most postcodes. Geelong, Ballarat and Bendigo on day 2–3. Gippsland and far-west VIC add a day." },
    TAS: { fullName: "Tasmania", std: "4–6", exp: "3–4", notes: "Includes a Bass Strait sea crossing. Hobart and Launceston typically arrive within 4–5 business days. Remote postcodes may require additional handling." }
  };

  function initMap(root) {
    var shell = root.querySelector("[data-ship-map]");
    if (!shell) return;
    var regions = Array.prototype.slice.call(shell.querySelectorAll("[data-region]"));
    var labels = Array.prototype.slice.call(shell.querySelectorAll("[data-region-label]"));
    if (!regions.length) return;

    var detail = {
      region: root.querySelector("[data-detail-region]"),
      name: root.querySelector("[data-detail-name]"),
      std: root.querySelector("[data-detail-std]"),
      exp: root.querySelector("[data-detail-exp]"),
      notes: root.querySelector("[data-detail-notes]"),
      warm: root.querySelector("[data-detail-warm]")
    };

    function setActive(code) {
      var d = REGION_DATA[code];
      if (!d) return;
      regions.forEach(function (el) {
        var c = el.getAttribute("data-region");
        el.classList.toggle("dim", c !== code);
        el.setAttribute("stroke", c === code ? "#043146" : "#ffffff");
        el.setAttribute("stroke-width", c === code ? "2.5" : "2");
      });
      labels.forEach(function (el) {
        el.style.opacity = el.getAttribute("data-region-label") === code ? "1" : "0.4";
      });
      if (detail.region) detail.region.textContent = d.isOrigin ? "Origin — fastest zone" : "Region";
      if (detail.name) detail.name.textContent = d.fullName;
      if (detail.std) detail.std.innerHTML = d.std + " <small>days</small>";
      if (detail.exp) detail.exp.innerHTML = d.exp + " <small>days</small>";
      if (detail.notes) detail.notes.innerHTML = "<b>Note · </b>" + d.notes;
      if (detail.warm) detail.warm.hidden = !d.isOrigin;
    }

    regions.forEach(function (el) {
      var code = el.getAttribute("data-region");
      el.addEventListener("mouseenter", function () { setActive(code); });
      el.addEventListener("click", function () { setActive(code); });
      el.addEventListener("focus", function () { setActive(code); });
    });

    setActive("SA");
  }

  /* ─────────── boot ─────────── */
  function boot() {
    Array.prototype.slice.call(document.querySelectorAll(".cdw-ship")).forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  // re-init when re-rendered in the theme editor
  document.addEventListener("shopify:section:load", boot);
})();
